import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogPosts } from '../data/blog-posts';
import { generateSlug, EnhancedBlogPost } from '../utils/blog-utils';

interface BlogPostType {
  id: string;
  title: string;
  date: string;
  category: string;
  summary: string;
  tags: string[] | never[];
  content: string;
  mermaid: string | null;
  language: string;
  image: string;
  slug?: string;
  lang?: string;
  mermaidDiagrams?: string[];
}

// Composant pour le sélecteur de langue avec drapeau
const LanguageSelector = ({ currentLang, postId }: { currentLang: string, postId: string }) => {
  // Trouver l'article dans l'autre langue
  const alternateLang = currentLang === 'fr' ? 'en' : 'fr';
  const alternateLangText = alternateLang === 'fr' ? 'FR' : 'EN';
  
  // Chercher un article avec le même ID mais dans l'autre langue
  const alternatePost = blogPosts.find(
    (post) => (post.id === postId.replace(/-en$/, '') || post.id === `${postId}-en`) 
             && post.language === alternateLang
  );
  
  if (!alternatePost) return null;
  
  const alternateSlug = generateSlug(alternatePost.id);
  
  return (
    <Link 
      to={`/blog/${alternateSlug}`}
      className="flex items-center px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs font-medium transition-colors"
    >
      <img 
        src={`${window.location.origin}/images/flags/${alternateLang}.svg`}
        alt={alternateLangText} 
        className="w-4 h-4 mr-1" 
      />
      {alternateLangText}
    </Link>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Trouver l'article correspondant au slug dans l'URL
  const post = blogPosts.find(
    (post) => {
      const postSlug = generateSlug(post.id);
      return postSlug === slug;
    }
  ) as BlogPostType | undefined;

  useEffect(() => {
    if (!post) {
      // Si l'article n'est pas trouvé, rediriger vers la page blog
      navigate('/blog');
    }
    
    // Essayer d'initialiser Mermaid si l'article contient un diagramme
    if (post && (post.mermaid || post.mermaidDiagrams)) {
      import('../utils/diagram-utils').then(module => {
        module.initMermaid().catch(console.error);
      });
    }
  }, [post, navigate]);

  if (!post) {
    return null; // Ne rien rendre pendant la redirection
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="container mx-auto px-4 py-16 max-w-4xl"
    >
      <article className="bg-white rounded-xl shadow-lg p-8 mb-8">
        <header className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <span className="text-blue-600 text-sm font-semibold">
              {post.category}
            </span>
            <div className="flex space-x-2">
              <span className="text-xs flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded">
                <img 
                  src={`${window.location.origin}/images/flags/${(post.language || post.lang || 'fr').toLowerCase()}.svg`}
                  alt={(post.language || post.lang || 'fr').toUpperCase()} 
                  className="w-4 h-4 mr-1" 
                />
                {(post.language || post.lang || 'fr').toUpperCase()}
              </span>
              <LanguageSelector 
                currentLang={(post.language || post.lang || 'fr').toLowerCase()} 
                postId={post.id} 
              />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">{post.title}</h1>
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags && post.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs bg-gray-100 px-2 py-1 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
          <time className="text-gray-500 text-sm block">
            {post.date}
          </time>
        </header>
        
        <div 
          className="prose prose-lg max-w-none article-content"
          style={{
            lineHeight: '1.8',
            fontSize: '1.1rem'
          }}
          dangerouslySetInnerHTML={{ __html: post.content || '' }}
        />
        
        <style dangerouslySetInnerHTML={{ __html: `
          .article-content p {
            margin-bottom: 1.5rem;
            line-height: 1.8;
            color: #374151;
          }
          
          .article-content h1,
          .article-content h2,
          .article-content h3 {
            margin-top: 2rem;
            margin-bottom: 1rem;
            font-weight: 600;
            color: #111827;
          }
          
          .article-content h1 {
            font-size: 2rem;
          }
          
          .article-content h2 {
            font-size: 1.5rem;
          }
          
          .article-content h3 {
            font-size: 1.25rem;
          }
          
          .article-content ul,
          .article-content ol {
            margin-bottom: 1.5rem;
            padding-left: 2rem;
          }
          
          .article-content li {
            margin-bottom: 0.5rem;
            line-height: 1.8;
          }
          
          .article-content img {
            max-width: 100%;
            height: auto;
            border-radius: 0.5rem;
            margin: 2rem auto;
            display: block;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          }
          
          .article-content blockquote {
            border-left: 4px solid #3B82F6;
            padding-left: 1rem;
            margin: 1.5rem 0;
            font-style: italic;
            color: #6B7280;
          }
          
          .article-content code {
            background-color: #F3F4F6;
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
            font-size: 0.875rem;
          }
          
          .article-content pre {
            background-color: #1F2937;
            color: #F9FAFB;
            padding: 1rem;
            border-radius: 0.5rem;
            overflow-x: auto;
            margin-bottom: 1.5rem;
          }
          
          .article-content pre code {
            background-color: transparent;
            padding: 0;
            color: inherit;
          }
          
          .article-content strong {
            font-weight: 600;
            color: #111827;
          }
          
          .article-content em {
            font-style: italic;
          }
          
          .article-content a {
            color: #3B82F6;
            text-decoration: underline;
            transition: color 0.2s;
          }
          
          .article-content a:hover {
            color: #2563EB;
          }
          
          .article-content hr {
            border: none;
            border-top: 1px solid #E5E7EB;
            margin: 2rem 0;
          }
          
          .article-content table {
            width: 100%;
            margin-bottom: 1.5rem;
            border-collapse: collapse;
          }
          
          .article-content th,
          .article-content td {
            padding: 0.75rem;
            border: 1px solid #E5E7EB;
            text-align: left;
          }
          
          .article-content th {
            background-color: #F9FAFB;
            font-weight: 600;
          }
          
          .article-content .generated-article-image {
            max-width: 80%;
            margin: 2rem auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }
        ` }} />
        
        {post.mermaid && (
          <div className="mermaid mt-8 bg-gray-50 p-4 rounded-lg">
            {post.mermaid}
          </div>
        )}
        
        {post.mermaidDiagrams && post.mermaidDiagrams.length > 0 && (
          <div className="mt-8">
            {post.mermaidDiagrams.map((diagram, index) => (
              <div 
                key={index} 
                className="mermaid mb-4 bg-gray-50 p-4 rounded-lg"
              >
                {diagram}
              </div>
            ))}
          </div>
        )}
      </article>
    </motion.div>
  );
};

export default BlogPost; 